import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import ruLocale from '@fullcalendar/core/locales/ru';
import interactionPlugin, {EventResizeDoneArg} from '@fullcalendar/interaction';
import {EventDropArg} from '@fullcalendar/core/index.js';

import {useNavigate} from 'react-router-dom';
import {FC, useEffect, useMemo} from 'react';

import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import Select from '@atomaro/ui-kit/components/Select/Select';
import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';

import {differenceInCalendarDays, format, formatDate, isToday} from 'date-fns';
import {ru} from 'date-fns/locale';

import {TEvent} from 'entities/calendar';
import {COLORS, ruFnsLocale} from 'shared/consts';
import {EmptyEl} from 'shared/ui/EmptyEl';
import {useCalendarCalendar} from 'features/calendar/lib/useCalendarCalendar';

import {
  CalendarCalendarActions,
  CalendarCalendarHead,
  CalendarCalendarTitle,
  CalendarCalendarWidgetTitle,
  CalendarCalendarWrapper,
  DayHeaderContentWrapper,
} from './styles';
import {CalendarDetail} from '../CalendarDetail';
import {CalendarNewEvent} from '../CalendarNewEvent';
import {toRem} from 'shared/helpers';
import {useUserStore} from 'entities/user';
import {getNowForUserTz} from 'features/calendar/helpers/dates.ts';

type CalendarCalendarProps = {
  activeDate: Date;
  events: TEvent[];
  isVidget?: boolean;
  onChangeFilters: (filters: {date?: Date; hiveIds?: string[]}) => void;
  onEventDrop: (arg: EventDropArg) => void;
  onEventResize: (arg: EventResizeDoneArg) => void;
  eventIdFromUrl?: string;
  onEventOpen?: () => void;
  openEventModal?: () => void;
};
export const CalendarCalendar: FC<CalendarCalendarProps> = ({
  activeDate,
  events,
  isVidget,
  onChangeFilters,
  onEventDrop,
  onEventResize,
  eventIdFromUrl,
  onEventOpen,
  openEventModal,
}) => {
  const {
    eventItems,
    isScreenLg,
    calendarRef,
    detailEvent,
    isdetailOpen,
    editId,
    editValues,
    isEditOpen,
    onPrevClick,
    onNextClick,
    onViewChange,
    setIsDetailOpen,
    onCalendarEventClick,
    onEditClick,
    setIsEditOpen,
    setEditId,
    onCalendarTouchStart,
    onCalendarTouchEnd,
    setDetailEvent,
    getApi,
  } = useCalendarCalendar({onChangeFilters, activeDate, events});
  const userTz = useUserStore((s) => s.userInfo?.timezone);

  // Обработка открытия события по id из URL
  useEffect(() => {
    if (eventIdFromUrl && events.length > 0 && !isdetailOpen) {
      const foundEvent = events.find(
        (event) => event.id === eventIdFromUrl || String(event.id) === eventIdFromUrl
      );
      if (foundEvent) {
        setDetailEvent(foundEvent);
        setTimeout(() => {
          setIsDetailOpen(true);
          // Удаляем параметр из URL после открытия
          onEventOpen?.();
        }, 300);
      }
    }
  }, [eventIdFromUrl, events, setDetailEvent, setIsDetailOpen, isdetailOpen, onEventOpen]);

  const navigate = useNavigate();

  const DayHeaderContent = ({date}: {date: Date}) => {
    return (
      <DayHeaderContentWrapper $isVidget={isVidget}>
        <span>{format(date, 'E', {locale: ruFnsLocale})}</span>
        <p className={differenceInCalendarDays(date, activeDate) === 0 ? '_active' : ''}>
          {format(date, 'dd', {locale: ruFnsLocale})}
        </p>
      </DayHeaderContentWrapper>
    );
  };

  const viewSelectItems: IDropdownMenuItem[] = [
    {
      key: 'timeGridDay',
      value: 'День',
      checkIcon: <EmptyEl />,
    },
    {
      key: 'timeGridWeek',
      value: 'Неделя',
      checkIcon: <EmptyEl />,
    },
    {
      key: 'dayGridMonth',
      value: 'Месяц',
      checkIcon: <EmptyEl />,
    },
    {
      key: 'listNext7',
      value: 'Расписание',
      checkIcon: <EmptyEl />,
    },
  ];

  const isTodayDate = useMemo(() => {
    return isToday(activeDate);
  }, [activeDate]);

  const selectedDateSelector = useMemo(() => {
    return formatDate(activeDate, 'yyyy-MM-dd');
  }, [activeDate]);

  let initialView = 'timeGridDay';
  if (!isScreenLg) initialView = 'timeGridWeek';
  if (isVidget) initialView = 'listWeek';

  return (
    <CalendarCalendarWrapper
      $isVidget={isVidget}
      $selectedDateSelector={selectedDateSelector}
      className="CalendarCalendarWrapper"
    >
      {isScreenLg && !isVidget && (
        <CalendarCalendarHead>
          <CalendarCalendarTitle>
            <span>{format(activeDate, 'LLLL', {locale: ru})}</span>
            <i>{activeDate.getFullYear()}</i>
          </CalendarCalendarTitle>
          <CalendarCalendarActions>
            {!isTodayDate && (
              <Button
                size="m"
                colorScheme="neutral"
                label="Сегодня"
                onClick={() => onChangeFilters({date: new Date()})}
              />
            )}
            <Button
              iconPrefix={
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.184291 4.55788L4.76762 0L5.64904 0.886341L1.51136 5.00105L5.64904 9.11576L4.76762 10.0021L0.184291 5.44422C0.0663268 5.32691 0 5.16741 0 5.00105C0 4.83469 0.0663268 4.67519 0.184291 4.55788Z"
                    fill={COLORS.NEUTRAL.DEFAULT}
                  />
                </svg>
              }
              size="m"
              colorScheme="neutral"
              onClick={() => onPrevClick()}
            />
            <Button
              iconPrefix={
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.46464 4.55788L0.881302 0L-0.000117302 0.886341L4.13757 5.00105L-0.000117302 9.11576L0.881302 10.0021L5.46464 5.44422C5.5826 5.32691 5.64893 5.16741 5.64893 5.00105C5.64893 4.83469 5.5826 4.67519 5.46464 4.55788Z"
                    fill={COLORS.NEUTRAL.DEFAULT}
                  />
                </svg>
              }
              size="m"
              colorScheme="neutral"
              onClick={() => onNextClick()}
            />
            <Select
              items={viewSelectItems}
              defaultValue={'timeGridDay'}
              onChange={onViewChange}
              placement="bottom"
            />
          </CalendarCalendarActions>
        </CalendarCalendarHead>
      )}
      {isVidget && (
        <CalendarCalendarWidgetTitle>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.5 4.5V6.5H17V4.5H18C18.8284 4.5 19.5 5.17157 19.5 6V8.01562L4.5 8.01562V6C4.5 5.17157 5.17157 4.5 6 4.5H7V6.5H8.5V4.5L15.5 4.5ZM4.5 9.51562L4.5 18C4.5 18.8284 5.17157 19.5 6 19.5H18C18.8284 19.5 19.5 18.8284 19.5 18V9.51562L4.5 9.51562ZM6 3H7V1H8.5V3L15.5 3V1H17V3H18C19.6569 3 21 4.34315 21 6V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V6C3 4.34315 4.34315 3 6 3Z"
              fill="#101828"
            />
          </svg>
          <span>Ближайшие события</span>
          <a onClick={() => navigate('/calendar')}>
            <span>Все</span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.5577 11.4695L10.0577 6L9 7.06361L13.9652 12.0013L9 16.9389L10.0577 18.0025L15.5577 12.5331C15.6993 12.3923 15.7789 12.2009 15.7789 12.0013C15.7789 11.8016 15.6993 11.6102 15.5577 11.4695Z"
                fill={COLORS.FG.SOFT}
                fillOpacity="0.55"
              />
            </svg>
          </a>
        </CalendarCalendarWidgetTitle>
      )}
      <div
        onTouchStart={onCalendarTouchStart}
        onTouchEnd={onCalendarTouchEnd}
        style={{
          height: isVidget ? `calc(100% - ${toRem(40)})` : 'auto',
        }}
      >
        <FullCalendar
          key={`tz-${userTz ?? 'local'}`}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={initialView}
          initialDate={activeDate}
          locale={ruLocale}
          headerToolbar={false}
          dayHeaderContent={(args) => DayHeaderContent(args)}
          nowIndicator
          now={() => getNowForUserTz()}
          events={eventItems}
          editable
          droppable
          views={{
            listNext7: {
              type: 'list',
              duration: {days: 7},
            },
          }}
          eventClick={onCalendarEventClick}
          eventDrop={onEventDrop}
          eventResize={onEventResize}
          dateClick={openEventModal}
        />
      </div>

      {detailEvent && (
        <CalendarDetail
          isOpen={isdetailOpen}
          onClose={() => setIsDetailOpen(false)}
          event={detailEvent}
          isMobile={!isScreenLg}
          onEditClick={onEditClick}
        />
      )}

      {editValues && editId && (
        <CalendarNewEvent
          isOpen={isEditOpen}
          editId={editId}
          defaultValues={editValues}
          onClose={() => {
            setIsEditOpen(false);
            setTimeout(() => {
              setEditId(null);
            }, 500);
          }}
        />
      )}
    </CalendarCalendarWrapper>
  );
};
